import axios from 'axios';
import { Auth, User, UserInfo, getAuth, signOut } from 'firebase/auth';
import { doc, DocumentSnapshot, getDoc } from 'firebase/firestore';
import { TenantAuthMethod } from '@esg/esg-global-types';
import { auth, db } from '../google/firebase';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

const SOURCE = 'auth.util.ts';

export interface AuthHeader {
  authorization: string;
}

export interface UserWithMfaInfo extends User {
  reloadUserInfo?: { mfaInfo: unknown };
}

/**
 * Generates an authorization header that is ready to add to http requests with the currently signed in user's JWT firebase auth token.
 * @returns {Promise<AuthHeader>}
 */
export const getAuthHeaderForUser = async (): Promise<AuthHeader> => {
  const auth: Auth = getAuth();
  const user: User | null = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user found');
  }

  const token = await user.getIdToken();
  return { authorization: `Bearer ${token}` };
};

const getErrorStatus = (error: unknown): number | null => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }

  // Handle Fetch API errors
  if (error instanceof Response) {
    return error.status;
  }

  // Handle Apollo Client network errors
  if (error && typeof error === 'object') {
    // Apollo network errors with statusCode property
    if ('statusCode' in error && typeof error.statusCode === 'number') {
      return error.statusCode;
    }

    // Generic status property check
    if ('status' in error && typeof error.status === 'number') {
      return error.status;
    }
  }

  return null;
};

export const handleAuthError = (error: unknown): void => {
  const status: number | null = getErrorStatus(error);

  // Only handle 401 Unauthorized errors
  if (status === 401) {
    try {
      signOut(auth).then(() => {
        location.replace('/');
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleAuthError'),
          {},
          tracking_id
        )
      );
    }
  }
};

/**
 * Determine whether a firebase auth user is logged in with the Microsoft SSO provider or not.
 * @param {User | null} auth_user User to check.
 * @returns {boolean}
 */
export const isMicrosoftUser = (auth_user: User | null): boolean => {
  return (
    auth_user?.providerData.some((provider: UserInfo) => provider.providerId === 'microsoft.com') ??
    false
  );
};

/**
 * Fetches the configured authentication method for the tenant domain derived from a user's email address.
 * @param {string} email The email address to extract the tenant domain from.
 * @returns {Promise<TenantAuthMethod | null>} The configured auth method, or null if the domain has no tenant record.
 */
export const getTenantDomainAuthMethodFromEmail = async (
  email: string
): Promise<TenantAuthMethod | null> => {
  const domain: string = email.toLowerCase().split('@')[1] ?? '';
  if (!domain) return null;
  const snapshot: DocumentSnapshot = await getDoc(doc(db, 'tenant_domains', domain));
  return snapshot.exists() ? (snapshot.data().auth_method as TenantAuthMethod) : null;
};
